import React, { useEffect, useState, useRef } from "react";
import {
  GoogleMap,
  InfoWindow
} from "@react-google-maps/api";
import { webSocketApiService } from "../../service/websocket.service";
import { localImages } from "../../common/localImages";
import { useGoogleMaps } from "../../contexts/GoogleMapsContext";
import ModernMarker from "./ModernMarker";

// Tracking data interfaces
interface VehicleData {
  vehicle_type: string;
  vehicle_no: string;
}

interface TrackingData {
  imei: string;
  latitude: number;
  longitude: number;
  timestamp: number;
  speed: number;
  battery_percent: number;
  vehicle_status: string;
  vehicle_data?: VehicleData;
  device_data?: {
    gps_model?: {
      model_name: string;
    };
  };
  external_voltage?: number;
  angle?: number;
  address?: string;
}

// Map setup
const containerStyle = {
  width: "100%",
  height: "100%",
};

const center = {
  lat: 10.956724,
  lng: 78.081329,
};

const mapOptions = {
  disableDefaultUI: false,
  zoomControl: true,
  mapTypeControl: false,
  gestureHandling: 'greedy',
  minZoom: 10,
  zoomAnimation: true,
  mapId: 'motosop-live-tracking-map', // Required for AdvancedMarkerElement
};

interface VehicleTrackingMapProps {
  onFirstDataReceived?: () => void;
  showMap?: boolean;
}

const VehicleTrackingMap: React.FC<VehicleTrackingMapProps> = ({ onFirstDataReceived, showMap = true }) => {
  const [latestData, setLatestData] = useState<TrackingData | null>(null);
  const [showInfoWindow, setShowInfoWindow] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState<string>("disconnected");
  const mapRef = useRef<google.maps.Map | null>(null);
  const initialLoadRef = useRef<boolean>(true);

  // Use centralized Google Maps context
  const { isLoaded, setMap } = useGoogleMaps();

  const onMapLoad = (map: google.maps.Map) => {
    mapRef.current = map;
    setMap(map); // Update the context with the map instance
  };

  useEffect(() => {
    const websocketUrl = "wss://api.motsop.com";
    const userId = localStorage.getItem("userId");

    const handleConnect = () => {
      console.log("✅ Connected to socket server");
      setConnectionStatus("connected");

      const socket = webSocketApiService.getSocket();
      if (socket) {
        socket.emit("joinLiveTracking");
        console.log("🟢 Automatically joined live-tracking channel.");
      }
    };

    const handleDisconnect = () => {
      console.warn("❌ Socket disconnected");
      setConnectionStatus("disconnected");
    };

    const handleError = (err: any) => {
      console.error("⚠️ Socket error:", err);
      setConnectionStatus("error");
    };

    webSocketApiService.init(websocketUrl);
    const socket = webSocketApiService.getSocket();

    if (socket) {
      socket.on("connect", handleConnect);
      socket.on("disconnect", handleDisconnect);
      socket.on("connect_error", handleError);

      socket.on("liveTrackingData", (data: TrackingData | TrackingData[]) => {
        console.log("📡 Received tracking data:", data);

        let newLatestData: TrackingData | null = null;

        if (Array.isArray(data) && data.length > 0) {
          const latest = data[data.length - 1];
          console.log("🎯 Latest array item coordinates - latitude:", latest.latitude, "longitude:", latest.longitude);
          setLatestData(latest);
          newLatestData = latest;
        } else if (!Array.isArray(data)) {
          console.log("🎯 Single data item coordinates - latitude:", data.latitude, "longitude:", data.longitude);
          setLatestData(data);
          newLatestData = data;
        }

        // Call the callback when first data is received
        if (newLatestData && onFirstDataReceived) {
          onFirstDataReceived();
        }

        if (initialLoadRef.current && mapRef.current && newLatestData) {
          mapRef.current.panTo({ lat: newLatestData.latitude, lng: newLatestData.longitude });
          initialLoadRef.current = false;
        }
      });
    }
    return () => {
      if (socket) {
        socket.off("liveTrackingData");
        socket.off("connect");
        socket.off("disconnect");
        socket.off("connect_error");
        webSocketApiService.close();
      }
    };
  }, []);

  const formatTimestamp = (timestamp: number): string => {
    return new Date(timestamp).toLocaleTimeString();
  };

  const [followVehicle, setFollowVehicle] = useState(true);

  useEffect(() => {
    if (followVehicle && latestData && latestData.latitude && latestData.longitude && mapRef.current) {
      mapRef.current.panTo({ lat: latestData.latitude, lng: latestData.longitude });
    }
  }, [latestData, followVehicle]);

  if (!showMap) {
    return (
      <div className="w-full h-full bg-blue-50 flex items-center justify-center text-blue-600 text-lg font-semibold">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-2"></div>
          <div>Loading tracking data...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full h-full relative rounded-lg shadow-lg border border-gray-200">
      {isLoaded && (
        <GoogleMap
          key={`map-${latestData?.latitude}-${latestData?.longitude}`}
          mapContainerStyle={containerStyle}
          center={latestData ? { lat: latestData.latitude, lng: latestData.longitude } : center}
          zoom={18}
          options={mapOptions}
          onLoad={onMapLoad}
        >
          {/* Modern Marker with AdvancedMarkerElement */}
          {latestData && (
            <>
              <ModernMarker
                position={{ lat: latestData.latitude, lng: latestData.longitude }}
                title={`Vehicle: ${latestData.vehicle_data?.vehicle_no || 'Unknown'}`}
                icon={localImages.locationPin}
                onMouseOver={() => setShowInfoWindow(true)}
                onMouseOut={() => setShowInfoWindow(false)}
                map={mapRef.current}
              />

              {/* Info Window */}
              {showInfoWindow && (
                <InfoWindow
                  position={{ lat: latestData.latitude, lng: latestData.longitude }}
                  onCloseClick={() => setShowInfoWindow(false)}
                >
                  <div className="p-2 text-sm space-y-1">
                    <div className="font-semibold text-gray-700">
                      {latestData.vehicle_data?.vehicle_no ?? "Unknown Vehicle"}
                    </div>
                    <div>Type: {latestData.vehicle_data?.vehicle_type}</div>
                    <div>Speed: {latestData.speed} km/h</div>
                    <div>Status: {latestData.vehicle_status}</div>
                    <div>Battery: {latestData.battery_percent}%</div>
                    <div>Time: {formatTimestamp(latestData.timestamp)}</div>
                    <div className="text-xs text-gray-500">
                      {latestData.latitude.toFixed(6)}, {latestData.longitude.toFixed(6)}
                    </div>
                    {latestData.address && (
                      <div className="text-xs text-gray-500">{latestData.address}</div>
                    )}
                  </div>
                </InfoWindow>
              )}
            </>
          )}
        </GoogleMap>
      )}

      {/* Info panel */}
      {latestData && (
        <div className="absolute top-4 left-4 bg-white bg-opacity-90 p-4 rounded-lg shadow-md w-64 text-sm space-y-1">
          <div className="font-semibold text-gray-700">
            {latestData.vehicle_data?.vehicle_no ?? "Unknown Vehicle"}
          </div>
          <div>Speed: {latestData.speed} km/h</div>
          <div>Status: <span className={`px-2 py-1 text-xs rounded ${latestData.vehicle_status === "PARKED"
            ? 'bg-yellow-100 text-yellow-800'
            : 'bg-green-100 text-green-800'
            }`}>{latestData.vehicle_status}</span></div>
          <div>Battery: {latestData.battery_percent}%</div>
          <div className="text-xs text-gray-500">
            {latestData.latitude ? latestData.latitude.toFixed(6) : 'N/A'}, {latestData.longitude ? latestData.longitude.toFixed(6) : 'N/A'}
          </div>

          {/* Connection status indicator */}
          <div className="mt-2">
            <span>Connection: </span>
            <span className={`text-xs font-bold px-2 py-1 rounded-md ${connectionStatus === "connected"
              ? "bg-green-100 text-green-700"
              : "bg-red-100 text-red-700"
              }`}>
              {connectionStatus.toUpperCase()}
            </span>
          </div>

          {/* Toggle button for "follow vehicle" mode */}
          <button
            onClick={() => setFollowVehicle(!followVehicle)}
            className={`mt-2 px-3 py-1 text-xs rounded-md ${followVehicle
              ? "bg-blue-500 text-white"
              : "bg-gray-200 text-gray-700"
              }`}
          >
            {followVehicle ? "Following Vehicle" : "Follow Vehicle"}
          </button>
        </div>
      )}
    </div>
  );
};

export default VehicleTrackingMap;
export { VehicleTrackingMap as SimpleLiveTrackingMap };